import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { ArrowForward as ArrowForwardIcon } from '@material-ui/icons';
import { ROUTE_CONSTANTS } from 'constants/route';
import { getTextContrastColor } from 'helpers/color';
import { formatCurrency } from 'helpers/number';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    fontFamily: 'Roboto Slab',
    backgroundColor: (props) => (props.day ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.65)'),
    boxShadow: (props) => (props.day ? '0px 0px 10px #FFFFFF' : '0px 0px 5px #000000'),
    borderRadius: '4px',
    maxWidth: '600px',
    textAlign: 'center',
    // border: (props) => (props.day ? '5px solid #000000' : `5px solid ${props.color}`),
  },
  cardHeader: {
    display: 'inline-flex',
    margin: '0 auto 1rem auto',
    textAlign: 'left',
  },
  insurerName: {
    flex: 1,
    fontSize: '1.4rem',
    color: (props) => (props.day ? '#606060' : props.color),
    textShadow: (props) => (props.day ? 'none' : `0px 0px 2px ${props.color}`),
    padding: '0.625rem 0.75rem 0 0',
    lineHeight: '1.5rem',
  },
  quarter: {
    flex: 1,
    fontSize: '0.875rem',
    color: (props) => (props.day ? '#606060' : 'rgba(255, 255, 255, 0.7)'),
    lineHeight: '0.875rem',
  },
  cardBody: {
    textAlign: 'left',
    padding: '0 0.75rem',
    color: (props) => (props.day ? '#606060' : 'rgba(255, 255, 255, 0.7)'),
    fontSize: '0.875rem',
  },
  dataPoint: {
    display: 'flex',
    marginBottom: '0.5rem',
  },
  pointName: {
    flex: 1,
    fontWeight: 'bold',
  },
  pointValue: {
    flex: 1,
    textAlign: 'right',
  },
  cardFooter: {
    paddingTop: '1rem',
  },
  cta: {
    backgroundColor: (props) => (props.day ? '#ff7c09' : props.color),
    color: (props) => (props.day ? '#fff' : getTextContrastColor(props.color)),
    boxShadow: (props) => (props.day ? 'none' : `0px 0px 2px ${props.color}`),
    fontFamily: 'Roboto Slab',
    textTransform: 'none',
    fontWeight: 'bold',
  },
}));

const InsurerDetails = ({
  insurer: {
    insurerId,
    insurerName: name,
    commercialPremium = 0,
    residentialPremium = 0,
    topCounty = '',
    topCoverageCode = '',
    totalPremium = 0,
  } = {},
  day = false,
  color,
  quarter,
  year,
}) => {
  const classes = useStyles({ day, color });
  const navigate = useNavigate();

  const goToInsurerProfilePage = useCallback(() => {
    navigate(`${ROUTE_CONSTANTS.INSURER}?insurers=${insurerId}&quarter=${quarter}&year=${year}`);
  }, [navigate, insurerId, quarter, year]);

  return name ? (
    <div className={classes.root}>
      <div className={classes.cardHeader}>
        <div className={classes.insurerName}>{name}</div>
      </div>
      <div className={classes.quarter}>
        Q{quarter} {year}
      </div>
      <div className={classes.cardBody}>
        <div className={classes.dataPoint}>
          <div className={classes.pointName}>Total Premium</div>
          <div className={classes.pointValue}>{formatCurrency(totalPremium)}</div>
        </div>
        <div className={classes.dataPoint}>
          <div className={classes.pointName}>Commercial Premium</div>
          <div className={classes.pointValue}>{formatCurrency(commercialPremium)}</div>
        </div>
        <div className={classes.dataPoint}>
          <div className={classes.pointName}>Residential Premium</div>
          <div className={classes.pointValue}>{formatCurrency(residentialPremium)}</div>
        </div>
        <div className={classes.dataPoint}>
          <div className={classes.pointName}>Top Coverage Code</div>
          <div className={classes.pointValue}>{topCoverageCode}</div>
        </div>
        <div className={classes.dataPoint}>
          <div className={classes.pointName}>Top County</div>
          <div className={classes.pointValue}>{topCounty}</div>
        </div>
      </div>
      <div className={classes.cardFooter}>
        <Button
          variant="contained"
          className={classes.cta}
          disableElevation
          endIcon={<ArrowForwardIcon />}
          onClick={goToInsurerProfilePage}
        >
          Learn more
        </Button>
      </div>
    </div>
  ) : null;
};

export default InsurerDetails;
